// src/pages/vs/VsHeyGenPage.js
// Cinematic monochrome redesign — mirrors VsDeepDubPage structure.
// HeyGen is an avatar-first platform; dubbing is a secondary feature.
// DubLabs is dubbing-first, purpose-built.
import React, { useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import useSEO from '../../hooks/useSEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const BREADCRUMBS = [
  { name: 'Compare', url: 'https://dublabs.ai/vs/heygen' },
  { name: 'vs HeyGen', url: 'https://dublabs.ai/vs/heygen' },
];

// ─── Data ─────────────────────────────────────────────────────────────────
const COMPARISON_ROWS = [
  {
    label: 'Product focus',
    dublabs:    { text: 'Dubbing-first — the whole platform is one job', win: 'us' },
    competitor: { text: 'Avatar-first; dubbing is a bolted-on Translate feature', win: 'them' },
  },
  {
    label: 'Lip-sync on your real footage',
    dublabs:    { text: 'Frame-accurate re-sync on the actual source video', win: 'us' },
    competitor: { text: 'Optimized for AI avatars, weaker on user-supplied video', win: 'them' },
  },
  {
    label: 'Voice preservation',
    dublabs:    { text: 'Clones your original speaker across every language', win: 'us' },
    competitor: { text: 'Voice library or clone (higher credit tier)', win: 'them' },
  },
  {
    label: 'Pricing model',
    dublabs:    { text: 'Transparent per-video usage', win: 'us' },
    competitor: { text: 'Subscription + credit meter — costs escalate with length', win: 'them' },
  },
  {
    label: 'Long-form consistency',
    dublabs:    { text: 'Same voice model across a full-length video', win: 'us' },
    competitor: { text: 'Drift and re-clone artifacts on longer clips', win: 'them' },
  },
  {
    label: 'Per-language calibration',
    dublabs:    { text: 'Each of 32 languages tuned individually — not one generic pipeline', win: 'us' },
    competitor: { text: 'Long language list; quality varies widely per language', win: 'them' },
  },
  {
    label: 'Turnaround',
    dublabs:    { text: 'Sub-4 minutes on short videos', win: 'tie' },
    competitor: { text: 'Minutes on short clips; slows on long-form', win: 'tie' },
  },
  {
    label: 'Language breadth',
    dublabs:    { text: '32, calibrated per language', win: 'tie' },
    competitor: { text: '175+ language codes; quality varies widely', win: 'tie' },
  },
  {
    label: 'AI avatar generation',
    dublabs:    { text: 'Not our product — we dub real people', win: 'them' },
    competitor: { text: 'Native — this is their core capability', win: 'them' },
  },
  {
    label: 'Best for',
    dublabs:    { text: 'Teams dubbing existing video at scale', win: 'us' },
    competitor: { text: 'Teams generating AI avatar video from a script', win: 'them' },
  },
];

const ADVANTAGES = [
  {
    title: 'Purpose-built for dubbing, not a side feature',
    body: 'Every model, workflow, and pricing decision at DubLabs is optimized for one job: taking a real video and making it sound native in another language. HeyGen is a great avatar platform — but their Translate feature was added after the fact, and it shows in the lip-sync fidelity, long-form consistency, and language-specific tuning.',
  },
  {
    title: 'Lip-sync on your source video, not on an avatar',
    body: "HeyGen's lip-sync shines because their avatars are AI-generated — the model controls the face. When you upload a real person, the accuracy drops. DubLabs is trained specifically to re-sync a real human's mouth movements frame-by-frame, so your CEO, teacher, or presenter looks like they actually spoke the new language.",
  },
  {
    title: 'Transparent per-video pricing',
    body: "You see what a dub costs before you upload it. HeyGen's subscription-plus-credits meter penalizes long-form content — a 30-minute video eats far more credits than four 7-minute videos, even though the work is similar. DubLabs charges per video, so long-form doesn't punish your economics.",
  },
  {
    title: "Per-language calibration, not one generic pipeline",
    body: "HeyGen advertises 175+ languages, but that breadth comes from a single generic pipeline stretched across every code. DubLabs invests in per-language tuning across all 32 of our supported languages — prosody, timing, and phonetics calibrated individually — so voices sound native instead of transliterated.",
  },
];

const USE_CASES = [
  {
    title: 'Choose DubLabs when:',
    items: [
      'You already have video and need it dubbed — not generated from scratch',
      'Long-form (10+ min) is a normal part of your library',
      'You want consistent per-video pricing without a credit meter',
      'You want per-language calibration, not one generic pipeline',
      'You need voice cloning + lip-sync as a first-class feature, not an add-on',
    ],
    highlight: true,
  },
  {
    title: 'Choose HeyGen when:',
    items: [
      'You want to generate AI avatar videos from a text script',
      'Your workflow starts with copy, not with existing footage',
      'You need personalized outbound video at scale (sales, prospecting)',
      'You prefer a subscription bundle over per-video pricing',
      'AI avatars are the primary output, dubbing is secondary',
    ],
    highlight: false,
  },
];

// ─── Reusable helpers ─────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 780, mx: 'auto', px: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {eyebrow && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', mb: 2.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{
          fontFamily: 'Inter', fontWeight: 900,
          fontSize: 'clamp(1.9rem, 4.5vw, 3.6rem)',
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em', mb: subtitle ? 2 : 0,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 300,
            fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
            color: 'rgba(255,255,255,0.45)', lineHeight: 1.6,
            maxWidth: 600, mx: 'auto',
          }}>
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
}

function WinIcon({ result, ours }) {
  if (result === 'tie') {
    return <RemoveIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 18 }} />;
  }
  if ((result === 'us' && ours) || (result === 'them' && !ours)) {
    return <CheckIcon sx={{ color: '#fff', fontSize: ours ? 22 : 18, opacity: ours ? 1 : 0.55 }} />;
  }
  return <CloseIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 16 }} />;
}

function ComparisonTable({ rows, competitorName }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.15, once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box sx={{
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(16px)',
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.62rem',
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', alignSelf: 'center',
          }}>
            Attribute
          </Typography>

          <Box sx={{
            textAlign: 'center', position: 'relative', py: 0.5,
            borderRadius: 1, background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' },
              color: '#fff', letterSpacing: '-0.01em',
            }}>
              DubLabs
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.55rem',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)', mt: 0.2,
            }}>
              Recommended
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: { xs: '0.9rem', md: '1rem' },
              color: 'rgba(255,255,255,0.6)', letterSpacing: '-0.005em',
            }}>
              {competitorName}
            </Typography>
          </Box>
        </Box>

        {rows.map((row, i) => {
          const ourAdvantage = row.dublabs.win === 'us';
          const isTie = row.dublabs.win === 'tie';
          return (
            <Box
              key={row.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
                px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
                borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                alignItems: 'center',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
              }}
            >
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 600,
                fontSize: { xs: '0.78rem', md: '0.9rem' },
                color: '#fff', letterSpacing: '-0.005em', pr: 1,
              }}>
                {row.label}
              </Typography>

              <Box sx={{
                textAlign: 'center', px: { xs: 0.5, md: 1.5 }, py: 0.5,
                borderRadius: 1,
                background: ourAdvantage ? 'rgba(255,255,255,0.04)' : 'transparent',
              }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 500,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: '#fff', lineHeight: 1.4, letterSpacing: '-0.005em',
                }}>
                  {row.dublabs.text}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', px: { xs: 0.5, md: 1.5 } }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours={false} />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 400,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: isTie ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.4)',
                  lineHeight: 1.4, letterSpacing: '-0.005em',
                }}>
                  {row.competitor.text}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2 },
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)', alignItems: 'center',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: '0.7rem',
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
          }}>
            Wins
          </Typography>
          <Box sx={{
            textAlign: 'center', py: 0.75, borderRadius: 1,
            background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: '#fff', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'us').length}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: 'rgba(255,255,255,0.4)', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'them').length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

function AdvantageCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 2) * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.35s ease, background 0.35s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.24)',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 500, fontSize: '0.6rem',
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.35)', mb: 1.5,
        }}>
          0{index + 1}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: 'clamp(1.15rem, 1.8vw, 1.35rem)',
          color: '#fff', letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.6,
        }}>
          {item.body}
        </Typography>
      </Box>
    </motion.div>
  );
}

function UseCaseCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: item.highlight
          ? '1px solid rgba(255,255,255,0.28)'
          : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: item.highlight ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        boxShadow: item.highlight ? '0 0 40px rgba(255,255,255,0.04)' : 'none',
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: '1.15rem',
          color: '#fff', letterSpacing: '-0.02em', mb: 3, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.6 }}>
          {item.items.map((line, i) => (
            <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckIcon sx={{
                color: item.highlight ? '#fff' : 'rgba(255,255,255,0.4)',
                fontSize: 16, mt: 0.4, flexShrink: 0,
              }} />
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 400, fontSize: '0.88rem',
                color: 'rgba(255,255,255,0.6)', lineHeight: 1.5,
              }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function VsHeyGenPage() {
  const navigate = useNavigate();

  useSEO({
    title: 'DubLabs vs HeyGen — AI Dubbing vs Avatar Platform Compared',
    description:
      'DubLabs vs HeyGen: DubLabs is purpose-built for dubbing existing video with perfect lip-sync and voice cloning. HeyGen focuses on AI avatars. Compare.',
    canonical: 'https://dublabs.ai/vs/heygen',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const goContact = () => navigate('/contact');

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh' }}>
      <Breadcrumbs items={BREADCRUMBS} />
      {/* Hero */}
      <Box sx={{
        position: 'relative',
        pt: { xs: 12, md: 18 },
        pb: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0) 55%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center', mb: 3,
            }}>
              Comparison · DubLabs vs HeyGen
            </Typography>

            <Typography component="h1" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              color: '#fff', lineHeight: 0.98, letterSpacing: '-0.05em',
              textAlign: 'center', mb: 3,
              maxWidth: 1000, mx: 'auto',
            }}>
              One job, done right.<br />Not one of many.
            </Typography>

            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.7vw, 1.25rem)',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.55, textAlign: 'center',
              maxWidth: 720, mx: 'auto', mb: 5,
            }}>
              HeyGen is an AI avatar platform — great for generating video from a script.
              DubLabs is a dubbing platform — built for taking your existing video and making
              it sound native in another language, with frame-accurate lip-sync on real people.
            </Typography>

            <Box sx={{
              display: 'flex', flexWrap: 'wrap',
              justifyContent: 'center', gap: 1.2, mb: 6,
            }}>
              {['Dubbing-First', 'Frame-Accurate Lip-Sync', 'Long-Form Consistent', 'Per-Video Pricing'].map((label) => (
                <Box key={label} sx={{
                  px: 2, py: 0.9,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(12px)',
                }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: '0.72rem',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.75)',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={goContact}
                sx={{
                  background: '#fff', color: '#000',
                  fontFamily: 'Inter', fontWeight: 700,
                  fontSize: '0.85rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 4, py: 1.6, borderRadius: '2px',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.88)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Comparison table */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Side-by-side"
            title="Where the differences matter"
            subtitle="Ordered by what actually changes the outcome for a team dubbing real video."
          />
          <ComparisonTable rows={COMPARISON_ROWS} competitorName="HeyGen" />
        </Container>
      </Box>

      {/* Where DubLabs wins */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Real Wedges"
            title="Where DubLabs wins"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {ADVANTAGES.map((adv, i) => (
              <AdvantageCard key={adv.title} item={adv} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Which is right */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Fit"
            title="Which is right for you?"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {USE_CASES.map((uc, i) => (
              <UseCaseCard key={uc.title} item={uc} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', mb: 2.5,
            }}>
              See it on your content
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              color: '#fff', lineHeight: 1.02,
              letterSpacing: '-0.045em', mb: 3,
            }}>
              Dub a real video.<br />Judge the lip-sync yourself.
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.55, mb: 5,
              maxWidth: 560, mx: 'auto',
            }}>
              Send us a clip, tell us the target language, and see the difference between a dubbing-first
              platform and an avatar tool with dubbing tacked on.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goContact}
              sx={{
                background: '#fff', color: '#000',
                fontFamily: 'Inter', fontWeight: 700,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 4, py: 1.6, borderRadius: '2px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.88)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Get in Touch
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
